import { Suspense, useState } from 'react';
import { SchemaForm } from '@/components/SchemaForm/SchemaForm';
import { t } from '@/lib/i18n';
import { useMyGrants, useInvokeConnector } from '../hooks';
import { CUSTOM_CONNECTOR_UIS } from '../custom-uis';
import styles from './ConnectorInvoker.module.css';

interface Props {
  connectorId: string;
}

type ParsedResult =
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'json'; value: unknown }
  | { type: 'text'; value: string };

function parseResult(raw: string): ParsedResult {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      typeof parsed[0] === 'object' &&
      parsed[0] !== null &&
      !Array.isArray(parsed[0])
    ) {
      const allScalar = (parsed as Record<string, unknown>[]).every((item) =>
        Object.values(item).every((v) => v === null || typeof v !== 'object'),
      );
      if (allScalar) {
        const headers = Object.keys(parsed[0] as object);
        const rows = (parsed as Record<string, unknown>[]).map((item) =>
          headers.map((h) => {
            const v = item[h];
            if (v === null || v === undefined) return '';
            if (typeof v === 'string') return v;
            if (typeof v === 'number' || typeof v === 'boolean') return String(v);
            return JSON.stringify(v);
          }),
        );
        return { type: 'table', headers, rows };
      }
    }
    return { type: 'json', value: parsed };
  } catch {
    return { type: 'text', value: raw };
  }
}

export function ConnectorInvoker({ connectorId }: Props) {
  const { data, isLoading } = useMyGrants();
  const [activeOp, setActiveOp] = useState<string | null>(null);
  const [result, setResult] = useState<ParsedResult | null>(null);
  const [invokeError, setInvokeError] = useState<string | null>(null);
  const [invokeStart, setInvokeStart] = useState<number | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);

  const connector = data?.connectors.find((c) => c.id === connectorId);
  const invoke = useInvokeConnector(connectorId);

  if (isLoading) {
    return <p className={styles.loading}>{t('Loading…')}</p>;
  }

  if (!connector) {
    return (
      <div className={styles.notFound}>
        <p>{t('Connector not found or you do not have access.')}</p>
      </div>
    );
  }

  if (connector.connectorStatus !== 'connected') {
    return (
      <div className={styles.notConfigured}>
        <p>
          {t(
            "This connector isn't set up yet. Ask your Admin to connect it in Settings → Connectors.",
          )}
        </p>
      </div>
    );
  }

  // Custom UI override
  const CustomUI = CUSTOM_CONNECTOR_UIS[connectorId];
  if (CustomUI) {
    const onInvoke = async (opName: string, input: Record<string, unknown>) => {
      const res = await invoke.mutateAsync({ operationName: opName, input });
      return res.result;
    };
    return (
      <Suspense fallback={<p className={styles.loading}>{t('Loading…')}</p>}>
        <CustomUI connector={connector} operations={connector.operations} onInvoke={onInvoke} />
      </Suspense>
    );
  }

  const currentOp =
    connector.operations.find((o) => o.name === activeOp) ?? connector.operations[0] ?? null;

  async function handleSubmit(values: Record<string, unknown>) {
    if (!currentOp) return;
    setResult(null);
    setInvokeError(null);
    setInvokeStart(Date.now());
    try {
      const res = await invoke.mutateAsync({ operationName: currentOp.name, input: values });
      setLatencyMs(Date.now() - (invokeStart ?? Date.now()));
      setResult(parseResult(res.result));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? t('Something went wrong. Try again.');
      setInvokeError(msg);
    }
  }

  return (
    <div className={styles.layout}>
      {/* Operation list */}
      <nav className={styles.opList} aria-label={t('Operations')}>
        <p className={styles.opListLabel}>{t('OPERATIONS')}</p>
        {connector.operations.map((op) => (
          <button
            key={op.name}
            className={`${styles.opItem} ${(activeOp ?? connector.operations[0]?.name) === op.name ? styles.opItemActive : ''}`}
            onClick={() => {
              setActiveOp(op.name);
              setResult(null);
              setInvokeError(null);
            }}
          >
            {op.name}
          </button>
        ))}
      </nav>

      {/* Form + result */}
      {currentOp ? (
        <div className={styles.invokeArea}>
          {/* Form column */}
          <div className={styles.formCol}>
            <h2 className={styles.opTitle}>{currentOp.name}</h2>
            {currentOp.description && <p className={styles.opDesc}>{currentOp.description}</p>}
            <SchemaForm
              key={currentOp.name}
              schema={
                currentOp.inputSchema as unknown as Parameters<typeof SchemaForm>[0]['schema']
              }
              onSubmit={(values) => void handleSubmit(values)}
              submitLabel={t('Run →')}
              isPending={invoke.isPending}
            />
          </div>

          {/* Result column */}
          <div className={styles.resultCol}>
            {!result && !invokeError && !invoke.isPending && (
              <p className={styles.resultEmpty}>{t('Run an operation to see results here.')}</p>
            )}
            {invoke.isPending && <p className={styles.loading}>{t('Running…')}</p>}
            {invokeError && (
              <div className={styles.resultError}>
                <p>{invokeError}</p>
              </div>
            )}
            {result && (
              <>
                <div className={styles.resultHeader}>
                  <span className={styles.resultTitle}>{t('Result')}</span>
                  {latencyMs !== null && (
                    <span className={styles.resultMeta}>
                      {result.type === 'table' ? `${result.rows.length} items · ` : ''}
                      {(latencyMs / 1000).toFixed(1)}s
                    </span>
                  )}
                  <button
                    className={styles.copyBtn}
                    onClick={() => {
                      void navigator.clipboard.writeText(
                        result.type === 'text'
                          ? result.value
                          : result.type === 'json'
                            ? JSON.stringify(result.value, null, 2)
                            : result.rows.map((r) => r.join('\t')).join('\n'),
                      );
                    }}
                  >
                    {t('Copy')}
                  </button>
                </div>

                {result.type === 'table' && (
                  <table className={styles.resultTable}>
                    <thead>
                      <tr>
                        {result.headers.map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {result.type === 'json' && (
                  <pre className={styles.resultJson}>{JSON.stringify(result.value, null, 2)}</pre>
                )}

                {result.type === 'text' && <pre className={styles.resultText}>{result.value}</pre>}
              </>
            )}
          </div>
        </div>
      ) : (
        <p className={styles.noOps}>{t('No operations available for this connector.')}</p>
      )}
    </div>
  );
}
